import { PageController } from '@client/components/_core/layout/PageController';
import { TreeNode } from '@client/components/_ui/display/TreeNode';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { confirmDestructive } from '@client/components/_ui/feedback/modal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { getStaleTime, trpc } from '@client/trpc/client';
import type { Key } from '@client/types/antd';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Skeleton, Tree } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useEffect, useState } from 'react';
import { type CostCodeAction, CostCodeEdit } from './CostCodeEdit';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/product-types/financials/$id/cost-codes',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Cost codes',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CostCodeSearch />
        </PageController>
    );
}

function updateTreeData(tree: DataNode[], key: Key, children: DataNode[]): DataNode[] {
    return tree.map((item) => {
        if (item.key === key) {
            return {
                ...item,
                children,
            };
        }

        if (item.children) {
            return {
                ...item,
                children: updateTreeData(item.children, key, children),
            };
        }

        return item;
    });
}

function findNodeByKey(tree: DataNode[], key: Key): DataNode | undefined {
    for (const item of tree) {
        if (item.key === key) {
            return item;
        }

        if (item.children) {
            const node = findNodeByKey(item.children, key);

            if (node) {
                return node;
            }
        }
    }
}

function CostCodeSearch() {
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { data: rootNode, isLoading: isLoadingRootNode } = trpc.costCode.getOrCreateRootCostCode.useQuery(
        { productTypeId: id },
        { staleTime: getStaleTime('infinite') },
    );
    const { mutate } = trpc.costCode.deleteCostCode.useMutation();

    const [data, setData] = useState<DataNode[]>([]);
    const [action, setAction] = useState<CostCodeAction>(['none']);
    const [parentId, setParentId] = useState<bigint | undefined>(undefined);

    useEffect(() => {
        if (rootNode?.id) {
            setData([
                {
                    key: rootNode.id,
                    title: (
                        <TreeNode
                            title="Root"
                            nodeKey={rootNode.id}
                            isRoot
                            onAdd={(parentId) => {
                                setParentId(parentId);
                                setAction(['add']);
                            }}
                        />
                    ),
                },
            ]);
        }
    }, [rootNode]);

    async function loadData({ key }: { key: any }) {
        const costCodes = await trpcUtils.costCode.listCostCodes.fetch({
            where: {
                parentId: key,
                productTypeId: id,
            },
        });

        setData((prev) =>
            updateTreeData(
                prev,
                key,
                costCodes.rows.map((item) => ({
                    key: item.id,
                    title: (
                        <TreeNode
                            title={item.name}
                            nodeKey={item.id}
                            isRoot={false}
                            onAdd={(parentId) => {
                                setParentId(parentId);
                                setAction(['add']);
                            }}
                            onEdit={(id) => {
                                setParentId(key);
                                setAction(['edit', id]);
                            }}
                            onDelete={(id) => {
                                confirmDestructive({
                                    title: 'Delete this item?',
                                    onOk() {
                                        message.loading('Deleting...');
                                        mutate(
                                            { id },
                                            {
                                                onSuccess() {
                                                    message.success('Deleted.');

                                                    void loadData({ key });
                                                },
                                                onError(error) {
                                                    errorMessage.show(error);
                                                },
                                            },
                                        );
                                    },
                                });
                            }}
                        />
                    ),
                    children: findNodeByKey(prev, item.id)?.children,
                })),
            ),
        );
    }

    if (isLoadingRootNode) {
        return <Skeleton active />;
    }

    return (
        <>
            <CostCodeEdit
                action={action}
                parentId={parentId}
                productTypeId={id}
                onClose={(parentIdToReload) => {
                    setAction(['none']);

                    if (parentIdToReload) {
                        void loadData({ key: parentIdToReload });
                    }
                }}
            />

            <Card>
                <Tree loadData={loadData} treeData={data} showLine selectable={false} />
            </Card>
        </>
    );
}
